import CalcInput from "@/components/calculators/CalcInput"
import ResultCard from "@/components/calculators/ResultCard"
import PageHero from "@/components/PageHero"
import SEOHead from "@/components/SEOHead"
import { formatCurrencyRounded, formatRatio, safeDivide, toNumber } from "@/lib/format"
import { useState } from "react"

function FacebookAdsRoas() {
  const [spend, setSpend] = useState("1000")
  const [ctr, setCtr] = useState("1.5")
  const [convRate, setConvRate] = useState("2.5")
  const [aov, setAov] = useState("60")

  const spendNum = toNumber(spend)
  const ctrNum = toNumber(ctr)
  const convRateNum = toNumber(convRate)
  const aovNum = toNumber(aov)

  const normalizedClicks = spendNum * (ctrNum / 100) * 10
  const conversions = normalizedClicks * (convRateNum / 100)
  const revenue = conversions * aovNum
  const roas = safeDivide(revenue, spendNum)
  const netProfit = revenue - spendNum

  return (
    <div>
      <SEOHead
        title="Facebook Ads ROAS Calculator"
        description="Estimate revenue, ROAS, and net profit for Meta and Facebook Ads campaigns using CTR, conversion rate, and average order value."
      />

      <PageHero
        eyebrow="Paid social"
        title="Facebook Ads ROAS Calculator"
        description="Estimate revenue and return on ad spend for your Meta / Facebook social campaigns."
      />

      <section className="max-w-5xl mx-auto px-4 sm:px-6 -mt-6 sm:-mt-8 pb-12">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-5 sm:p-7">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 mb-5">Campaign inputs</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <CalcInput
                  label="Ad Spend"
                  prefix="$"
                  value={spend}
                  onChange={setSpend}
                  placeholder="1000"
                  tooltip="Total budget spent on the Meta / Facebook campaign being modeled."
                />
                <CalcInput
                  label="Link Click-Through-Rate"
                  suffix="%"
                  value={ctr}
                  onChange={setCtr}
                  placeholder="1.5"
                  tooltip="Percentage of ad impressions that resulted in a link click (Link CTR in Meta Ads Manager)."
                />
                <CalcInput
                  label="Conversion Rate"
                  suffix="%"
                  value={convRate}
                  onChange={setConvRate}
                  placeholder="2.5"
                  tooltip="Percentage of link clicks that resulted in a completed purchase or goal conversion."
                />
                <CalcInput
                  label="Avg. Order Value"
                  prefix="$"
                  value={aov}
                  onChange={setAov}
                  placeholder="60"
                  tooltip="Average revenue generated per completed order."
                />
              </div>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 mb-5">Estimated results</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <ResultCard label="Estimated Revenue" value={formatCurrencyRounded(revenue)} formula="Est. Conversions x Avg. Order Value" />
                <ResultCard label="ROAS" value={formatRatio(roas)} emphasis formula="Estimated Revenue / Ad Spend" />
                <ResultCard label="Net Profit" value={formatCurrencyRounded(netProfit)} formula="Estimated Revenue - Ad Spend" />
              </div>
              <p className="mt-4 text-xs text-slate-400 leading-relaxed">
                This is a simplified estimation model. Actual results vary by audience, placement, and attribution settings.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-16">
        <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight mb-4">iOS Privacy, Attribution & What Counts as "Good"</h2>
        <p className="text-[15px] text-slate-600 leading-relaxed mb-4">
          Since Apple's App Tracking Transparency (ATT) framework rolled out, Meta's reported ROAS can undercount
          real performance because fewer conversions are attributed back to ads, especially on iOS devices. Many
          advertisers now see a gap between Meta's in-platform reporting and their actual store or CRM revenue.
        </p>
        <p className="text-[15px] text-slate-600 leading-relaxed">
          Attribution windows (commonly 7-day click / 1-day view) also shape which conversions get credited to an
          ad. On paid social, a "good" ROAS is generally considered 2:1 or higher for competitive e-commerce
          verticals, with 4:1+ viewed as a strong, scalable result once creative and audience are optimized.
        </p>
      </section>
    </div>
  )
}

export default FacebookAdsRoas
